import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Container, Table, ProgressBar } from 'react-bootstrap';
import { Props } from 'react-select';

import AWSS3Multipart from '@uppy/aws-s3-multipart';
import { Uppy, UppyFile } from '@uppy/core';
import { DragDrop as DragDropComponent } from '@uppy/react';

import styles from './Analyze.module.scss';
import { root, upload } from './apis';

import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import { CompleteUploadDTO } from 'orbus-uploader-client';
import {
	allowedFileTypes,
	allowedFileExtensions,
	isSupportedFile
} from 'orbus-uploader-common/dist/fileTypes';
import { FaFilePdf, FaFileCode, FaFile } from 'react-icons/fa';
import { VscJson } from 'react-icons/vsc';
import Select from 'react-select/async';
import { Socket } from 'socket.io-client';

function extname(name: string) {
	return name.split('.').pop();
}

function profileIsQuickCheck(profileName: string): boolean {
	return profileName.endsWith('.cfg');
}

function profileIsKnownMultiPgOutput(pName: string): boolean {
	return ['createHardCoverTemplate.kfpx', 'generateTemplate.kfpx'].includes(
		pName
	);
}

/*
function profileIsNormal(profileName: string): boolean {
	return profileName.endsWith('.kfpx');
}
*/

// https://github.com/JedWatson/react-select/issues/2902#issuecomment-595670371
type ExtractDefaultType<P> = P extends Props<infer T> ? T : never;
type OptionTypeBase = ExtractDefaultType<Props>;

interface FileStatus {
	phase: string;
	progress: number;
	files: string[];
}

interface JobStatusMap {
	[jobId: string]: FileStatus;
}

const TableRow = React.memo(function TableRow(props: {
	name: string;
	status: string;
	value: number;
	jobId: string;
	uuidJobId: string;
	files: string[];
	selectedProfile?: string;
}) {
	// The bucket contains the jobs' files under its uuidJobId, not its internalJobId (or jobId)

	const { name, status, value, jobId, uuidJobId, files = [] } = props;

	const selectedProfile = props.selectedProfile;

	console.log('TableRow - got selectedProfile of', selectedProfile);

	console.log(
		'profileIsKnownMultiPgOutput(selectedProfile) ->',
		selectedProfile
			? profileIsKnownMultiPgOutput(selectedProfile)
			: '(no profile value to check)'
	);

	// console.log('table row data;', JSON.stringify(props))

	const origFile = files.find(f => f.startsWith('original.'));
	const outputFile = files.find(f => f.startsWith('output.'));
	const origExtension = origFile && /.*\.(\w+)/.exec(origFile)?.[1];

	// TODO - should we remove the ability to just directly call these GETs?
	// It should definitely not be allowed in production, right?.
	async function handleOrigClick(e: React.MouseEvent) {
		e.preventDefault();
		const url = await authenticatedFetchUrlForOpening(
			root + `/api/download/${uuidJobId}/original.pdf`
		);
		window.open(
			// ${origFile as string}`,
			url,
			'_blank',
			'noopener'
		);
	}

	async function handleXmlClick(e: React.MouseEvent) {
		e.preventDefault();
		const url = await authenticatedFetchUrlForOpening(
			root + `/api/download/${uuidJobId}/report.xml`
		);
		window.open(url, '_blank', 'noopener');
	}

	async function handlePdfReportClick(e: React.MouseEvent) {
		e.preventDefault();
		const url = await authenticatedFetchUrlForOpening(
			root + `/api/download/${uuidJobId}/report.pdf`
		);
		window.open(url, '_blank', 'noopener');
	}

	/** Note: The parameter `frontEndExpectsMultipleFiles` is simply to help the front end decide how it should
	 * "receive" the call of the API. The API will be called at the same endpoint with a GET request.
	 */
	async function handleDlOutputLink(
		e: React.MouseEvent,
		frontEndExpectsMultipleFiles?: true
	) {
		e.preventDefault();
		if (!frontEndExpectsMultipleFiles) {
			const url = await authenticatedFetchUrlForOpening(
				root + `/api/download/${uuidJobId}/output.pdf`
			);
			window.open(url, '_blank', 'noopener');
		} else {
			await fetch(root + `/api/download/${uuidJobId}/output.pdf`, {
				headers: [['x-uploader-key', process.env?.REACT_APP_API_KEY || '']]
			})
				.then(res => {
					console.log('response from API -', res.status, res.statusText);
					if (res.status >= 200 && res.status <= 399) {
						// API still uses 302 for this endpoint in both cases
						return res.blob();
						// window.open(
						// 	root + `/api/download/${uuidJobId}/output?type=multiple`,
						// 	'_blank',
						// 	'noopener'
						// );
					}
				})
				.then(blob => {
					console.log('success - .zip file blob received');
					if (blob) {
						const fileUrl = window.URL.createObjectURL(blob);
						// window.location.assign(fileUrl)
						const tempFileLink = document.createElement('a');
						tempFileLink.href = fileUrl;
						tempFileLink.download = `output_files_zip_${
							outputFiles?.length ? `${outputFiles.length}_files` : ''
						}.zip`;
						tempFileLink.click();
						// console.log("opening .zip fileUrl now, at", fileUrl);
						// window.open(
						// 	fileUrl,
						// 	'_blank',
						// 	'noopener'
						// );
					}
				})
				.catch((err: any) => {
					console.log(err);
				});
		}
	}

	async function authenticatedFetchUrlForOpening(url: string) {
		return await fetch(url, {
			headers: [['x-uploader-key', process.env?.REACT_APP_API_KEY || '']],
			method: 'GET'
		}).then(res => {
			console.log('Got url in response:', res.url || '(no url...)');
			return res.url;
		});
	}

	async function handleJsonClick(e: React.MouseEvent) {
		e.preventDefault();
		const url = await authenticatedFetchUrlForOpening(
			root + `/api/download/${uuidJobId}/report.json`
		);
		window.open(url, '_blank', 'noopener');
	}

	async function handleSimpleJsonClick(e: React.MouseEvent) {
		e.preventDefault();
		const url = await authenticatedFetchUrlForOpening(
			root + `/api/download/${uuidJobId}/report.simple.json`
		);
		window.open(url, '_blank', 'noopener');
	}

	const outputFiles = useMemo(
		() => files.filter(file => file.startsWith('outputfiles/')),
		[files]
	);

	const [thumbImgUrl, setThumbImgUrl] = useState('');

	useEffect(() => {
		// async function fetchThumbImg() {
		// 	return await fetch(root + `/api/download/${uuidJobId}/thumbnails/1.png`, {
		// 		headers: [
		// 			['x-uploader-key', process.env?.REACT_APP_API_KEY || '']
		// 		],
		// 		method: "GET"
		// 	}).then(res => res.url)
		// }

		if (status === 'complete') {
			// fetchThumbImg().then(url => {
			// 	console.log('got thumb img url as:', url)
			// 	setThumbImgUrl(url)
			// })
			const encodedUri = encodeURI(
				root +
					`/api/download/${uuidJobId}/thumbnails/1.png?v=${
						process.env.REACT_APP_API_KEY || ''
					}`
			);
			setThumbImgUrl(encodedUri);
		} else {
			setThumbImgUrl('');
		}
	}, [status]);

	// useEffect(() => {
	// 	console.log('[debug] - thumbImgUrl change:', thumbImgUrl)
	// }, [thumbImgUrl])

	return (
		<tr
			className={
				status === 'complete'
					? styles.tableRowComplete
					: styles.tableRowIncomplete
			}
		>
			<td className={styles.nameColumn} style={{ whiteSpace: 'nowrap' }}>
				{name}
			</td>
			<td
				className={styles.infoColumn}
				style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
			>
				{status === 'complete' ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							textAlign: 'center'
						}}
					>
						<img
							alt="thumbnail"
							className={styles.thumbnail}
							src={thumbImgUrl}
						/>
						<div className={styles.linkContainer}>
							<button className={styles.dlLink} onClick={handleOrigClick}>
								{origExtension === 'pdf' ? <FaFilePdf /> : <FaFile />} original{' '}
								{origExtension}
							</button>
							<br />
							<button className={styles.dlLink} onClick={handleXmlClick}>
								<FaFileCode /> report xml
							</button>
							<button className={styles.dlLink} onClick={handlePdfReportClick}>
								<FaFilePdf /> report pdf
							</button>
							<br />
							{outputFiles.length > 1 ? (
								<div
									style={{
										backgroundColor: 'rgb(230,230,230)',
										border: 'solid 1px black'
									}}
								>
									<h4>{outputFiles.length} output files available</h4>
									<>
										<button
											className={styles.dlLink}
											onClick={async e => await handleDlOutputLink(e, true)}
										>
											<FaFilePdf />
											Download output files zip
										</button>
										<br />
									</>
								</div>
							) : (
								<>
									<button
										className={styles.dlLink}
										onClick={async e =>
											handleDlOutputLink(
												e,
												selectedProfile &&
													profileIsKnownMultiPgOutput(selectedProfile)
													? true
													: undefined
											)
										}
									>
										<FaFilePdf />
										Download output pdf
									</button>
									<br />
								</>
							)}
							{files.includes('output.json') ? (
								<>
									<button
										className={styles.dlLink}
										onClick={e => handleDlOutputLink(e, undefined)}
									>
										<VscJson /> output json
									</button>
									<br />
								</>
							) : (
								<></>
							)}
							<button className={styles.dlLink} onClick={handleJsonClick}>
								<FaFileCode /> report json
							</button>
							<button className={styles.dlLink} onClick={handleSimpleJsonClick}>
								<FaFileCode /> report json (simplified)
							</button>
						</div>
					</div>
				) : status === 'error' ? (
					<ProgressBar label="error" now={100} striped variant="danger" />
				) : (
					<ProgressBar
						animated
						label={`${status}: ${Math.round(value * 100)}%`}
						now={Math.round(value * 100)}
						variant={status === 'uploading' ? 'success' : undefined}
					/>
				)}
			</td>
		</tr>
	);
});

const uppy = new Uppy({
	meta: { type: 'avatar' },
	autoProceed: true,
	onBeforeFileAdded(file) {
		const mime = file.type;
		const name = file.name;
		const ext = extname(name);
		if (!mime) throw new Error('Could not determine file type');
		else if (!ext) throw new Error('Could not determine file extension');
		return isSupportedFile({ mime, ext });
	}
});

const dashProps = {
	uppy: uppy,
	showProgressDetails: true
};

interface AnalyzeProps {
	socket: Socket;
}

function Analyze(props: AnalyzeProps) {
	const { socket } = props;
	const [fileStatus, setFileStatus] = useState<JobStatusMap>({});

	const loadOptions = useCallback(async (input = '') => {
		const profiles: string[] = await fetch(
			root + `/api/upload/list-profiles?prefix=${escape(input)}`,
			{
				headers: [['x-uploader-key', process.env?.REACT_APP_API_KEY || '']]
			}
		).then(res => res.json() as Promise<string[]>);

		const options = profiles.map(p => ({
			value: p,
			label: `${
				p === 'default.kfpx'
					? 'default.kfpx (Default, Normal)'
					: `${p} (${profileIsQuickCheck(p) ? 'Quick Check' : 'Normal'})`
			}`.concat(
				profileIsKnownMultiPgOutput(p) ? '\u0020[Outputs multiple files]' : ''
			)
		}));

		const defaultProfile =
			options.find(p => p.value === 'default.kfpx') || options[0];
		setSelectedProfile(defaultProfile);

		return options;
	}, []);

	const [analyzeOnly, setAnalyzeOnly] = useState(false);

	const [shouldDeleteFiles, setShouldDeleteFiles] = useState(true);

	const toggleOutput = useCallback(() => setAnalyzeOnly(v => !v), [
		setAnalyzeOnly
	]);

	const toggleShouldDeleteFiles = useCallback(
		() => setShouldDeleteFiles(v => !v),
		[setShouldDeleteFiles]
	);

	// useEffect(() => {
	// console.log('shouldDeleteFiles change:', shouldDeleteFiles);
	// }, [shouldDeleteFiles]);

	const [selectedProfile, setSelectedProfile] = useState<{
		value: string;
		label: string;
	}>();

	function handleChange(value: any) {
		// https://github.com/JedWatson/react-select/issues/2902#issuecomment-481194330
		if (value) setSelectedProfile(value);
		else setSelectedProfile(undefined);
	}

	useEffect(() => {
		uppy.use(AWSS3Multipart, {
			id: 'AWSPlugin',
			limit: 0,
			async createMultipartUpload(file) {
				const { type } = file;
				const ext = extname(file.name);
				if (!type) throw new Error('Unable to determine file type');
				else if (!ext) throw new Error('Unable to determine file extension');
				else if (!isSupportedFile({ mime: type, ext }))
					throw new Error('Unsupported file type');
				const result = await upload.createUpload({
					originalName: file.name,
					type
				});
				console.log(
					'createMultipartUpload for file',
					file.name,
					'-',
					JSON.stringify(file),
					'- result with key:',
					JSON.stringify(result)
				);

				return {
					uploadId: result.UploadId,
					key: result.Key
				};
			},
			listParts(_file, data) {
				return upload.listParts(data);
			},
			async prepareUploadPart(_file, partData) {
				const result = await upload.signPart({
					uploadId: partData.uploadId,
					partNumber: partData.number,
					key: partData.key
				});

				return {
					method: 'PUT',
					url: result.url
				};
			},
			abortMultipartUpload(_file, data) {
				return upload.abortUpload(data);
			},
			completeMultipartUpload: async (file, data) => {
				if (!file.type) throw new Error('Could not get file type');
				const completePost: CompleteUploadDTO = {
					...data,
					analyzeOnly,
					deleteInput: shouldDeleteFiles,
					startThumbnails: 1,
					endThumbnails: 1,
					type: file.type,
					originalFilename: file.name
				};

				console.log(
					'completeMultipartUpload - value of completePost -',
					JSON.stringify(completePost)
				);

				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				const profileFname = selectedProfile?.value as string;
				if (profileIsQuickCheck(profileFname)) {
					completePost.quickCheckProfile = profileFname;
				} else {
					completePost.profile =
						profileFname === 'default.kfpx' ? undefined : profileFname;
				}

				const result = (await upload.completeUpload(completePost)) || {};
				const internalJobId = result.internalJobId;
				const uuidJobId = result.jobId;
				console.log('completeUpload id results:', JSON.stringify(result));
				uppy.setFileMeta(file.id, {
					jobId: internalJobId,
					uuidJobId: uuidJobId
				});
				// uppy.setFileMeta(file.id, { jobId: result.internalJobId });
				return {};
			}
		});
		const uploadProgressHandler = (
			file: UppyFile,
			progress: { bytesUploaded: number; bytesTotal: number }
		) => {
			setFileStatus(prevState => ({
				...prevState,
				...{
					[file.id]: {
						phase: 'uploading',
						progress: progress.bytesUploaded / progress.bytesTotal,
						files: []
					}
				}
			}));
		};
		const uploadSuccessHandler = (f: UppyFile) => {
			const file = uppy.getFile<{ jobId: string; uuidJobId: string }>(f.id);
			setFileStatus(prevState => ({
				...prevState,
				...{
					[file.id]: {
						phase: 'waiting',
						progress: 0,
						files: []
					}
				}
			}));

			const emitWatch = () => {
				socket.emit('preflight-watch', file.meta.jobId);
			};

			emitWatch();

			socket.on('connect', emitWatch);

			const cleanupHandlers = () => {
				socket.off('preflight-progress', progressHandler);
				socket.off('preflight-complete', completedHandler);
				socket.off('preflight-failure', failureHandler);
				socket.off('connect', emitWatch);
			};

			const progressHandler = (jobId: string, progress: number) => {
				const eventFile = uppy
					.getFiles<{ jobId: string; uuidJobId: string }>()
					.find(uf => uf.meta.jobId === jobId);
				if (!eventFile) console.error('Cannot find file');
				else {
					const value = progress;
					setFileStatus(prevState => ({
						...prevState,
						...{
							[eventFile.id]: {
								phase: 'processing',
								progress: value,
								files: []
							}
						}
					}));
				}
			};
			const completedHandler = (jobId: string, filesOutput: string[]) => {
				console.log(
					'completedHandler - jobId:',
					jobId,
					'filesOutput:',
					JSON.stringify(filesOutput)
				);
				const eventFile = uppy
					.getFiles<{ jobId: string; uuidJobId: string }>()
					.find(uf => uf.meta.jobId === jobId);
				if (!eventFile) console.error('Cannot find file');
				else {
					setFileStatus(s => ({
						...s,
						...{
							[eventFile.id]: {
								phase: 'complete',
								progress: 1,
								files: filesOutput
							}
						}
					}));
				}

				//cleanupHandlers();
			};
			const failureHandler = (jobId: string) => {
				const eventFile = uppy
					.getFiles<{ jobId: string; uuidJobId: string }>()
					.find(uf => uf.meta.jobId === jobId);
				if (!eventFile) console.error('Cannot find file');
				else {
					setFileStatus(prevState => ({
						...prevState,
						...{
							[eventFile.id]: {
								phase: 'error',
								progress: 0,
								files: []
							}
						}
					}));
				}

				socket.on('preflight-progress', progressHandler);
			};
			socket.on('preflight-progress', progressHandler);
			socket.on('preflight-complete', completedHandler);
			socket.on('preflight-failure', failureHandler);
		};
		const restrictionFailedHandler = (file: UppyFile, err: Error) => {
			if (err.message.startsWith('You can only upload:')) {
				alert(
					`Unsupported file type!\n\nYou selected:\n${
						file.type as string
					}\n\nOnly the following are supported:\n${allowedFileTypes.join(
						'\n'
					)}\n${allowedFileExtensions.join('\n')}`
				);
			} else {
				alert(err.message);
			}
		};
		const uploadErrorHandler = (file: UppyFile, err: Error) => {
			console.log(
				'NOTE TO FUTURE SELF: do you have any privacy extensions enabled? last time an upload was mysteriously failing it was caused by the "ClearURLs" firefox extension.'
			);
			console.log('Upload error for file', file.name, '-', err);
			setFileStatus(prevState => ({
				...prevState,
				...{
					[file.id]: {
						phase: 'error',
						progress: 0,
						files: []
					}
				}
			}));
		};
		uppy.on('restriction-failed', restrictionFailedHandler);
		uppy.on('upload-progress', uploadProgressHandler);
		uppy.on('upload-success', uploadSuccessHandler);
		uppy.on('upload-error', uploadErrorHandler);
		return () => {
			const AWSPlugin: AWSS3Multipart = uppy.getPlugin('AWSPlugin');
			uppy.removePlugin(AWSPlugin);
			uppy.off('upload-progress', uploadProgressHandler);
			uppy.off('upload-success', uploadSuccessHandler);
			uppy.off('restriction-failed', restrictionFailedHandler);
			uppy.off('upload-error', uploadErrorHandler);
		};
	}, [selectedProfile, analyzeOnly, shouldDeleteFiles, socket]);

	const files = uppy.getFiles<{ jobId: string; uuidJobId: string }>();
	let display;
	if (files.length === 0)
		display = (
			<>
				<div
					style={{
						height: 'min-content',
						color: 'rgb(120,120,120)',
						position: 'relative'
					}}
				>
					<h6
						style={{
							fontSize: '0.6em',
							margin: 0,
							position: 'relative',
							top: '0.33em',
							left: '1em'
						}}
					>
						KEY
					</h6>
					<h6 style={{ fontWeight: 'normal', marginLeft: '1em' }}>
						profile name&emsp;(normal/quick check)&emsp;[notes, if any]{' '}
					</h6>
				</div>
				<Select
					onChange={handleChange}
					loadOptions={loadOptions}
					defaultOptions
					value={selectedProfile}
					placeholder="Select a profile..."
				/>
				<br />
				<input
					style={{ userSelect: 'none', marginRight: '0.33em' }}
					id="createOutput"
					type="checkbox"
					checked={analyzeOnly}
					onChange={toggleOutput}
				/>
				<label style={{ userSelect: 'none' }} htmlFor="createOutput">
					Analyze only
				</label>
				<br />
				<input
					style={{ userSelect: 'none' }}
					id="deleteFilesCheckbox"
					type="checkbox"
					checked={shouldDeleteFiles}
					onChange={toggleShouldDeleteFiles}
				/>
				<label
					style={{ userSelect: 'none', marginRight: '0.33em' }}
					htmlFor="deleteFilesCheckbox"
				>
					Delete input files from S3 after run
				</label>
				<br />
				<DragDropComponent {...dashProps} />
			</>
		);
	else {
		display = (
			<Table className={styles.table}>
				<thead>
					<tr>
						<th>File name</th>
						<th>Progress</th>
					</tr>
				</thead>
				<tbody>
					{files.map(file => {
						return (
							<TableRow
								uuidJobId={file.meta.uuidJobId}
								jobId={file.meta.jobId}
								key={file.id}
								name={file.name}
								status={fileStatus[file.id]?.phase}
								value={fileStatus[file.id]?.progress || 0}
								files={fileStatus[file.id]?.files}
								selectedProfile={selectedProfile?.value}
							/>
						);
					})}
				</tbody>
			</Table>
		);
	}
	return <Container>{display}</Container>;
}

export default Analyze;
